import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  Shield, 
  Car, 
  Heart,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  Save,
  ShieldCheck,
  Calendar,
  Upload,
  FileText,
  User,
  Trash2
} from 'lucide-react';

const LICENCE_CONFIGS = {
  drivers_licence: {
    label: "Driver's Licence",
    icon: Car,
    color: "blue",
    fields: ['number', 'state', 'licence_class', 'expiry_date', 'notes']
  },
  security_licence: {
    label: "Security Licence",
    icon: Shield,
    color: "orange",
    fields: ['number', 'state', 'licence_type', 'expiry_date', 'notes']
  },
  first_aid: {
    label: "First Aid Certificate",
    icon: Heart,
    color: "red",
    fields: ['provider', 'certificate_number', 'expiry_date', 'notes']
  }
};

const AU_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const DRIVER_CLASSES = ['C', 'LR', 'MR', 'HR', 'HC', 'MC'];
const SECURITY_TYPES = [
  'Class 1A - Unarmed Guard',
  'Class 1B - Bodyguard',
  'Class 1C - Crowd Controller',
  'Class 1D - Armed Guard',
  'Class 2 - Technical Surveillance',
  'Master Licence'
];

function LicenceStatusBadge({ status }) {
  if (!status) return null;
  
  const configs = {
    expired: { label: 'Expired', className: 'bg-red-500/20 text-red-400 border-red-500/50', icon: XCircle },
    expiring: { 
      label: status.severity === 'critical' ? 'Expiring Soon!' : 'Expiring', 
      className: status.severity === 'critical' 
        ? 'bg-red-500/20 text-red-400 border-red-500/50'
        : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      icon: AlertTriangle 
    },
    valid: { label: 'Valid', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50', icon: CheckCircle2 },
    not_set: { label: 'Not Set', className: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50', icon: Clock }
  };
  
  const config = configs[status.status] || configs.not_set;
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.className} text-xs px-2 py-1 border`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
      {status.days_until_expiry !== null && status.status !== 'not_set' && (
        <span className="ml-1">({status.days_until_expiry}d)</span>
      )}
    </Badge>
  );
}

function LicenceCard({ type, data, isAdmin, onSave, onVerify, isSaving }) {
  const config = LICENCE_CONFIGS[type];
  const Icon = config.icon;
  const licenceData = data?.data || {};
  const status = data?.status;
  
  // Memoize the data string to prevent infinite re-renders
  const licenceDataStr = JSON.stringify(licenceData);
  
  const [formData, setFormData] = useState({
    number: licenceData.number || '',
    state: licenceData.state || '',
    licence_class: licenceData.licence_class || '',
    licence_type: licenceData.licence_type || '',
    provider: licenceData.provider || '',
    certificate_number: licenceData.certificate_number || '',
    expiry_date: licenceData.expiry_date ? licenceData.expiry_date.split('T')[0] : '',
    notes: licenceData.notes || ''
  });
  
  const [hasChanges, setHasChanges] = useState(false);
  
  useEffect(() => {
    const parsedData = JSON.parse(licenceDataStr);
    setFormData({
      number: parsedData.number || '',
      state: parsedData.state || '',
      licence_class: parsedData.licence_class || '',
      licence_type: parsedData.licence_type || '',
      provider: parsedData.provider || '',
      certificate_number: parsedData.certificate_number || '',
      expiry_date: parsedData.expiry_date ? parsedData.expiry_date.split('T')[0] : '',
      notes: parsedData.notes || ''
    });
    setHasChanges(false);
  }, [licenceDataStr]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };
  
  const handleSave = () => {
    const saveData = { ...formData };
    if (saveData.expiry_date) {
      saveData.expiry_date = new Date(saveData.expiry_date).toISOString();
    }
    onSave(type, saveData);
    setHasChanges(false);
  };
  
  return (
    <Card className="bg-[#1a1a1d] border-[#3f3f46]">
      <CardHeader className="pb-2 pt-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-rajdhani text-base flex items-center gap-2">
            <Icon className={`w-5 h-5 text-${config.color}-400`} />
            {config.label}
          </CardTitle>
          <div className="flex items-center gap-2">
            <LicenceStatusBadge status={status} />
            {licenceData.verified && (
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/50 text-xs px-2 py-1 border">
                <ShieldCheck className="w-3 h-3 mr-1" />
                Verified
              </Badge>
            )}
          </div>
        </div>
        {/* Verification metadata */}
        {licenceData.last_verified_at && (
          <p className="text-[10px] text-zinc-500 text-right" data-testid="last-verified-at">
            Last verified: {new Date(licenceData.last_verified_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            {licenceData.verification_source ? ` (${licenceData.verification_source})` : ''}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {type === 'drivers_licence' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Licence Number</Label>
                <Input
                  value={formData.number}
                  onChange={(e) => handleChange('number', e.target.value)}
                  className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                  placeholder="e.g., 12345678"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">State</Label>
                <Select value={formData.state || undefined} onValueChange={(v) => handleChange('state', v)}>
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46] h-8 text-sm">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    {AU_STATES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Licence Class</Label>
                <Select value={formData.licence_class || undefined} onValueChange={(v) => handleChange('licence_class', v)}>
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46] h-8 text-sm">
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    {DRIVER_CLASSES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Expiry Date</Label>
                <Input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => handleChange('expiry_date', e.target.value)}
                  className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                />
              </div>
            </div>
          </>
        )}
        
        {type === 'security_licence' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Licence Number</Label>
                <Input
                  value={formData.number}
                  onChange={(e) => handleChange('number', e.target.value)}
                  className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                  placeholder="e.g., 408-xxx-xxx"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">State</Label>
                <Select value={formData.state || undefined} onValueChange={(v) => handleChange('state', v)}>
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46] h-8 text-sm">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    {AU_STATES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Licence Type</Label>
                <Select value={formData.licence_type || undefined} onValueChange={(v) => handleChange('licence_type', v)}>
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46] h-8 text-sm">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    {SECURITY_TYPES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Expiry Date</Label>
                <Input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => handleChange('expiry_date', e.target.value)}
                  className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                />
              </div>
            </div>
          </>
        )}
        
        {type === 'first_aid' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Provider</Label>
                <Input
                  value={formData.provider}
                  onChange={(e) => handleChange('provider', e.target.value)}
                  className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                  placeholder="e.g., St John Ambulance"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Certificate Number</Label>
                <Input
                  value={formData.certificate_number}
                  onChange={(e) => handleChange('certificate_number', e.target.value)}
                  className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Expiry Date</Label>
              <Input
                type="date"
                value={formData.expiry_date}
                onChange={(e) => handleChange('expiry_date', e.target.value)}
                className="bg-[#09090b] border-[#3f3f46] h-9 w-1/2"
              />
            </div>
          </>
        )}
        
        <div className="space-y-1.5">
          <Label className="text-zinc-400 text-xs">Notes</Label>
          <Textarea
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            className="bg-[#09090b] border-[#3f3f46] min-h-[42px] text-sm"
            placeholder="Additional notes..."
          />
        </div>
        
        <div className="flex items-center justify-between pt-2">
          <div>
            {isAdmin && licenceData.number && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onVerify(type, !licenceData.verified)}
                className={`border-zinc-600 ${licenceData.verified ? 'text-yellow-400' : 'text-emerald-400'}`}
              >
                <ShieldCheck className="w-4 h-4 mr-2" />
                {licenceData.verified ? 'Unverify' : 'Verify'}
              </Button>
            )}
          </div>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            size="sm"
            className="bg-blue-500 hover:bg-blue-600"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function UserLicencesTab({ userId, userName }) {
  const { api, isAdmin } = useAuth();
  const [licences, setLicences] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedDocType, setSelectedDocType] = useState('security_licence');
  
  // Compliance form state
  const [complianceForm, setComplianceForm] = useState({
    employee_id: '',
    driver_licence_number: '',
    driver_licence_expiry: '',
    security_licence_number: '',
    security_licence_expiry: '',
    first_aid_number: '',
    first_aid_expiry: '',
  });
  const [complianceChanged, setComplianceChanged] = useState(false);
  
  const fetchLicences = useCallback(async () => {
    try {
      setLoading(true);
      const [licencesRes, userRes] = await Promise.all([
        api.get(`/licences/${userId}`),
        api.get(`/users/${userId}`)
      ]);
      setLicences(licencesRes.data);
      setUserData(userRes.data);
      
      // Populate compliance form
      const user = userRes.data;
      setComplianceForm({
        employee_id: user.employee_id || '',
        driver_licence_number: user.driver_licence_number || '',
        driver_licence_expiry: user.driver_licence_expiry ? user.driver_licence_expiry.split('T')[0] : '',
        security_licence_number: user.security_licence_number || '',
        security_licence_expiry: user.security_licence_expiry ? user.security_licence_expiry.split('T')[0] : '',
        first_aid_number: user.first_aid_number || '',
        first_aid_expiry: user.first_aid_expiry ? user.first_aid_expiry.split('T')[0] : '',
      });
      setComplianceChanged(false);
    } catch (error) {
      console.error('Error fetching licences:', error);
      toast.error('Failed to load licence data');
    } finally {
      setLoading(false);
    }
  }, [api, userId]);
  
  useEffect(() => {
    fetchLicences();
  }, [fetchLicences]);
  
  const handleComplianceChange = (field, value) => {
    setComplianceForm(prev => ({ ...prev, [field]: value }));
    setComplianceChanged(true);
  };
  
  const handleSaveCompliance = async () => {
    try {
      setSaving(true);
      
      const payload = {
        employee_id: complianceForm.employee_id || null,
        driver_licence_number: complianceForm.driver_licence_number || null,
        driver_licence_expiry: complianceForm.driver_licence_expiry ? new Date(complianceForm.driver_licence_expiry).toISOString() : null,
        security_licence_number: complianceForm.security_licence_number || null,
        security_licence_expiry: complianceForm.security_licence_expiry ? new Date(complianceForm.security_licence_expiry).toISOString() : null,
        first_aid_number: complianceForm.first_aid_number || null,
        first_aid_expiry: complianceForm.first_aid_expiry ? new Date(complianceForm.first_aid_expiry).toISOString() : null,
      };
      
      await api.put(`/users/${userId}`, payload);
      toast.success('Compliance info updated');
      setComplianceChanged(false);
      fetchLicences();
    } catch (error) {
      console.error('Error saving compliance:', error);
      toast.error(error.response?.data?.detail || 'Failed to save compliance info');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveLicence = async (type, data) => {
    try {
      setSaving(true);
      await api.put(`/licences/${userId}/${type}`, data);
      toast.success(`${LICENCE_CONFIGS[type].label} updated`);
      fetchLicences();
    } catch (error) {
      console.error('Error saving licence:', error);
      toast.error(error.response?.data?.detail || 'Failed to save licence');
    } finally {
      setSaving(false);
    }
  };
  
  const handleVerifyLicence = async (type, verified) => {
    try {
      setSaving(true);
      await api.post(`/licences/${userId}/${type}/verify`, { verified });
      toast.success(`${LICENCE_CONFIGS[type].label} ${verified ? 'verified' : 'unverified'}`);
      fetchLicences();
    } catch (error) {
      console.error('Error verifying licence:', error);
      toast.error(error.response?.data?.detail || 'Failed to verify licence');
    } finally {
      setSaving(false);
    }
  };
  
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: PDF, JPEG, PNG');
      return;
    }
    
    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/users/${userId}/upload-document?doc_type=${selectedDocType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document uploaded successfully');
      fetchLicences();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return null;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysUntil = Math.floor((expiry - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) {
      return { status: 'expired', className: 'text-red-400', label: 'Expired' };
    } else if (daysUntil <= 30) {
      return { status: 'expiring', className: 'text-yellow-400', label: `${daysUntil}d` };
    } else {
      return { status: 'valid', className: 'text-emerald-400', label: `${daysUntil}d` };
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Compliance & Employee Info Card */}
      <Card className="bg-[#1a1a1d] border-[#3f3f46]">
        <CardHeader className="pb-2 pt-3">
          <CardTitle className="font-rajdhani text-base flex items-center gap-2">
            <User className="w-5 h-5 text-blue-400" />
            Employee & Compliance Info
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Employee ID <span className="text-amber-400">(unique)</span></Label>
              <Input
                value={complianceForm.employee_id}
                onChange={(e) => handleComplianceChange('employee_id', e.target.value)}
                className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                placeholder="e.g., EMP001"
                data-testid="employee-id-input"
              />
            </div>
          </div>
          
          <div className="border-t border-[#3f3f46] pt-2">
            <Label className="text-zinc-400 text-xs mb-3 block">Quick Licence References</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-zinc-500 text-xs flex items-center gap-1">
                  <Car className="w-3 h-3" /> Driver Licence #
                </Label>
                <Input
                  value={complianceForm.driver_licence_number}
                  onChange={(e) => handleComplianceChange('driver_licence_number', e.target.value)}
                  className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                  placeholder="Licence number"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-500 text-xs flex items-center gap-1">
                  Expiry
                  {complianceForm.driver_licence_expiry && (
                    <span className={getExpiryStatus(complianceForm.driver_licence_expiry)?.className}>
                      ({getExpiryStatus(complianceForm.driver_licence_expiry)?.label})
                    </span>
                  )}
                </Label>
                <Input
                  type="date"
                  value={complianceForm.driver_licence_expiry}
                  onChange={(e) => handleComplianceChange('driver_licence_expiry', e.target.value)}
                  className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-500 text-xs flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Security Licence #
                </Label>
                <Input
                  value={complianceForm.security_licence_number}
                  onChange={(e) => handleComplianceChange('security_licence_number', e.target.value)}
                  className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                  placeholder="Licence number"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-500 text-xs flex items-center gap-1">
                  Expiry
                  {complianceForm.security_licence_expiry && (
                    <span className={getExpiryStatus(complianceForm.security_licence_expiry)?.className}>
                      ({getExpiryStatus(complianceForm.security_licence_expiry)?.label})
                    </span>
                  )}
                </Label>
                <Input
                  type="date"
                  value={complianceForm.security_licence_expiry}
                  onChange={(e) => handleComplianceChange('security_licence_expiry', e.target.value)}
                  className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-500 text-xs flex items-center gap-1">
                  <Heart className="w-3 h-3" /> First Aid #
                </Label>
                <Input
                  value={complianceForm.first_aid_number}
                  onChange={(e) => handleComplianceChange('first_aid_number', e.target.value)}
                  className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                  placeholder="Certificate number"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-500 text-xs flex items-center gap-1">
                  Expiry
                  {complianceForm.first_aid_expiry && (
                    <span className={getExpiryStatus(complianceForm.first_aid_expiry)?.className}>
                      ({getExpiryStatus(complianceForm.first_aid_expiry)?.label})
                    </span>
                  )}
                </Label>
                <Input
                  type="date"
                  value={complianceForm.first_aid_expiry}
                  onChange={(e) => handleComplianceChange('first_aid_expiry', e.target.value)}
                  className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSaveCompliance}
              disabled={!complianceChanged || saving}
              size="sm"
              className="bg-blue-500 hover:bg-blue-600"
              data-testid="save-compliance-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Compliance Info
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Document Upload Card */}
      <Card className="bg-[#1a1a1d] border-[#3f3f46]">
        <CardHeader className="pb-2 pt-3">
          <CardTitle className="font-rajdhani text-base flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-400" />
            Compliance Documents
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <Select value={selectedDocType} onValueChange={setSelectedDocType}>
              <SelectTrigger className="bg-[#09090b] border-[#3f3f46] w-[200px]">
                <SelectValue placeholder="Document Type" />
              </SelectTrigger>
              <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                <SelectItem value="driver_licence">Driver Licence</SelectItem>
                <SelectItem value="security_licence">Security Licence</SelectItem>
                <SelectItem value="first_aid">First Aid Certificate</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
            />
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="upload-document-btn"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload Document
            </Button>
          </div>
          
          <p className="text-xs text-zinc-500">
            Allowed: PDF, JPEG, PNG. Max size: 10MB
          </p>
          
          {/* Uploaded Documents List */}
          {userData?.documents?.length > 0 && (
            <div className="border-t border-[#3f3f46] pt-2 mt-2">
              <Label className="text-zinc-400 text-xs mb-3 block">Uploaded Documents</Label>
              <div className="space-y-1.5">
                {userData.documents.map((doc, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-2 bg-[#09090b] rounded border border-[#3f3f46]"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-zinc-500" />
                      <div>
                        <span className="text-sm text-zinc-300">{doc.file_name}</span>
                        <span className="text-xs text-zinc-500 ml-2">
                          ({doc.doc_type?.replace(/_/g, ' ')})
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-zinc-500">
                      {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Detailed Licence Cards */}
      <div className="grid gap-4">
        {Object.entries(LICENCE_CONFIGS).map(([type, config]) => (
          <LicenceCard
            key={type}
            type={type}
            data={licences?.licences?.[type]}
            isAdmin={isAdmin}
            onSave={handleSaveLicence}
            onVerify={handleVerifyLicence}
            isSaving={saving}
          />
        ))}
      </div>
    </div>
  );
}

export default UserLicencesTab;
